import { create } from 'zustand';
import { ScoredEvent } from '@/types/event';
import { VehicleSearchResult } from '@/types/vehicle';
import { PersonSearchResult } from '@/types/person';
import type { QueryParams } from '@/hooks/useQuery';

type ActionBarMode = 'browse' | 'selection';

export type QuerySimplicityLevel = 'sql' | 'subject_only' | 'complex';

export interface AnalyzedQueryInfo {
    subject?: string;
    appearance?: string;
    activity?: string;
    preprocessedText?: string;
    rawText?: string;
    hasAttachment?: boolean;
    isStatic?: boolean;
    licensePlate?: string;
    ocrText?: string;
    simplicityLevel: QuerySimplicityLevel;
    classIds: number[];
}

export interface SuggestedQuery {
    text: string;
    useAttachment: boolean;
}

export interface SimilarResult {
    scoredEvent: ScoredEvent;
    description: string;
}

interface QueryResultsState {
    mode: ActionBarMode;
    isSelectionMode: boolean;
    events: ScoredEvent[];
    eventPageMap: Map<string, number>;
    currentPage: number;
    selectedEventIds: Set<string>;
    lastQueryParams: QueryParams | null;
    lastExecutedKey: string | null;
    skipCache: boolean;
    demo: boolean;
    hasMore: boolean;
    cursor: string | null;
    queryLoading: boolean;
    analyzedQueries: AnalyzedQueryInfo[] | null;
    vehicleResults: VehicleSearchResult[] | null;
    personResults: PersonSearchResult[] | null;
    querySummary: string[] | null;
    querySuggestions: SuggestedQuery[];
    similarResults: SimilarResult[];
    loadMoreSummary: string[] | null;
    scrollPosition: number;
    enterSelectionMode: () => void;
    exitSelectionMode: () => void;
    setEvents: (events: ScoredEvent[]) => void;
    addEvent: (event: ScoredEvent) => void;
    removeEvent: (eventId: string) => void;
    removeEventsBySourceId: (sourceId: string) => void;
    clearEvents: () => void;
    incrementPage: () => void;
    setLastQueryParams: (params: QueryParams) => void;
    setLastExecutedKey: (key: string) => void;
    setSkipCache: (skip: boolean) => void;
    setDemo: (apply: boolean) => void;
    setHasMore: (hasMore: boolean) => void;
    setQueryLoading: (loading: boolean) => void;
    setAnalyzedQueries: (queries: AnalyzedQueryInfo[] | null) => void;
    setVehicleResults: (results: VehicleSearchResult[] | null) => void;
    setPersonResults: (results: PersonSearchResult[] | null) => void;
    setQuerySummary: (summary: string[] | null) => void;
    setQuerySuggestions: (suggestions: SuggestedQuery[]) => void;
    setSimilarResults: (results: SimilarResult[]) => void;
    setCursor: (cursor: string | null) => void;
    setLoadMoreSummary: (summary: string[] | null) => void;
    setScrollPosition: (position: number) => void;
    selectAll: (eventIds: string[]) => void;
    deselectEvents: (eventIds: string[]) => void;
    toggleEventSelection: (eventId: string) => void;
    clearSelection: () => void;
}

export const useQueryResultsStore = create<QueryResultsState>((set, get) => ({
    mode: 'browse',
    get isSelectionMode() {
        return get().mode === 'selection';
    },
    events: [],
    eventPageMap: new Map(),
    currentPage: 0,
    selectedEventIds: new Set(),
    lastQueryParams: null,
    lastExecutedKey: null,
    skipCache: false,
    demo: false,
    hasMore: false,
    cursor: null,
    queryLoading: false,
    analyzedQueries: null,
    vehicleResults: null,
    personResults: null,
    querySummary: null,
    querySuggestions: [],
    similarResults: [],
    loadMoreSummary: null,
    scrollPosition: 0,
    enterSelectionMode: () => set({ mode: 'selection' }),
    exitSelectionMode: () =>
        set({
            mode: 'browse',
            selectedEventIds: new Set(),
        }),
    setEvents: (events) => set({ events }),
    addEvent: (scoredEvent) =>
        set((state) => {
            if (
                state.events.some(
                    (existing) => existing.event.id === scoredEvent.event.id,
                )
            ) {
                return state;
            }
            const newPageMap = new Map(state.eventPageMap);
            newPageMap.set(scoredEvent.event.id, state.currentPage);
            return {
                events: [...state.events, scoredEvent],
                eventPageMap: newPageMap,
            };
        }),
    removeEvent: (eventId) =>
        set((state) => {
            const newSelectedIds = new Set(state.selectedEventIds);
            newSelectedIds.delete(eventId);
            const newPageMap = new Map(state.eventPageMap);
            newPageMap.delete(eventId);
            return {
                events: state.events.filter(
                    (scoredEvent) => scoredEvent.event.id !== eventId,
                ),
                selectedEventIds: newSelectedIds,
                eventPageMap: newPageMap,
            };
        }),
    removeEventsBySourceId: (sourceId) =>
        set((state) => {
            const eventsToRemove = new Set(
                state.events
                    .filter(
                        (scoredEvent) =>
                            scoredEvent.event.sourceId === sourceId,
                    )
                    .map((scoredEvent) => scoredEvent.event.id),
            );
            const newSelectedIds = new Set<string>(
                Array.from(state.selectedEventIds).filter(
                    (eventId) => !eventsToRemove.has(eventId),
                ),
            );
            const newPageMap = new Map<string, number>(
                Array.from(state.eventPageMap.entries()).filter(
                    ([eventId]) => !eventsToRemove.has(eventId),
                ),
            );
            return {
                events: state.events.filter(
                    (scoredEvent) => scoredEvent.event.sourceId !== sourceId,
                ),
                selectedEventIds: newSelectedIds,
                eventPageMap: newPageMap,
            };
        }),
    clearEvents: () =>
        set({
            events: [],
            selectedEventIds: new Set(),
            eventPageMap: new Map(),
            currentPage: 0,
            hasMore: false,
            cursor: null,
            analyzedQueries: null,
            vehicleResults: null,
            personResults: null,
            querySummary: null,
            querySuggestions: [],
            similarResults: [],
            loadMoreSummary: null,
            scrollPosition: 0,
        }),
    incrementPage: () =>
        set((state) => ({ currentPage: state.currentPage + 1 })),
    setLastQueryParams: (params) => set({ lastQueryParams: params }),
    setLastExecutedKey: (key) => set({ lastExecutedKey: key }),
    setSkipCache: (skip) => set({ skipCache: skip }),
    setDemo: (apply) => set({ demo: apply }),
    setHasMore: (hasMore) => set({ hasMore }),
    setQueryLoading: (loading) => set({ queryLoading: loading }),
    setAnalyzedQueries: (queries) => set({ analyzedQueries: queries }),
    setVehicleResults: (results) => set({ vehicleResults: results }),
    setPersonResults: (results) => set({ personResults: results }),
    setQuerySummary: (summary) => set({ querySummary: summary }),
    setQuerySuggestions: (suggestions) =>
        set({ querySuggestions: suggestions }),
    setSimilarResults: (results) => set({ similarResults: results }),
    setCursor: (cursor) => set({ cursor }),
    setLoadMoreSummary: (summary) => set({ loadMoreSummary: summary }),
    setScrollPosition: (position) => set({ scrollPosition: position }),
    selectAll: (eventIds) => set({ selectedEventIds: new Set(eventIds) }),
    deselectEvents: (eventIds) =>
        set((state) => ({
            selectedEventIds: new Set(
                Array.from(state.selectedEventIds).filter(
                    (id) => !eventIds.includes(id),
                ),
            ),
        })),
    toggleEventSelection: (eventId) =>
        set((state) => {
            const selectedEventIds = new Set(state.selectedEventIds);
            if (selectedEventIds.has(eventId)) {
                selectedEventIds.delete(eventId);
            } else {
                selectedEventIds.add(eventId);
            }
            return { selectedEventIds };
        }),
    clearSelection: () => set({ selectedEventIds: new Set() }),
}));
